import { forwardRef, useCallback } from 'react';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { UseTableAddRowColumnConfig } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-add-row-column-button';
import { useTableAddRowColumn } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-add-row-column-button';
import type { ButtonProps } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';

export interface TableAddRowColumnButtonProps extends Omit<ButtonProps, 'type'>, UseTableAddRowColumnConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
}

/**
 * Button component for adding a table row/column in a Tiptap editor.
 *
 * For custom button implementations, use the `useTableAddRowColumn` hook instead.
 */
export const TableAddRowColumnButton = forwardRef<HTMLButtonElement, TableAddRowColumnButtonProps>(
  (
    {
      editor: providedEditor,
      index,
      orientation,
      side,
      tablePos,
      hideWhenUnavailable = false,
      onAdded,
      text,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isVisible, handleAdd, label, canAddRowColumn, Icon } = useTableAddRowColumn({
      editor,
      index,
      orientation,
      side,
      tablePos,
      hideWhenUnavailable,
      onAdded,
    });

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleAdd();
      },
      [handleAdd, onClick],
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        disabled={!canAddRowColumn}
        data-style="ghost"
        data-active-state="off"
        data-disabled={!canAddRowColumn}
        role="button"
        tabIndex={-1}
        aria-label={label}
        aria-pressed={false}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </Button>
    );
  },
);

TableAddRowColumnButton.displayName = 'TableAddRowColumnButton';
